/**
 * Model catalog for the Excel multi-hop executor.
 *
 * Patterned on data-feed's agent config: one array entry per selectable model,
 * carrying everything the request builder needs. Adding a model is a single
 * entry; adding a provider is one adapter file plus one `switch` case in
 * `providers/index.ts`.
 */

import type { AIProvider } from "../../shared/config/models";

export type ExecutorReasoningEffort = "minimal" | "low" | "medium" | "high";

export interface ExecutorModelConfig {
  /** Stable selector used by `resolveExecutorModel` and diagnostics. */
  id: string;
  label: string;
  provider: AIProvider;
  /** Provider-specific wire model id. */
  model: string;
  reasoningEffort: ExecutorReasoningEffort;
  /** Overrides the adapter's default output cap. */
  maxOutputTokens?: number;
}

export const EXECUTOR_MODELS: readonly ExecutorModelConfig[] = [
  {
    id: "gpt-6-astra",
    label: "GPT-6 Astra",
    provider: "openai",
    model: "gpt-6-astra",
    reasoningEffort: "medium",
  },
  {
    id: "gpt-5.5",
    label: "GPT-5.5",
    provider: "openai",
    model: "gpt-5.5-2026-04-23",
    reasoningEffort: "low",
  },
  {
    // Plain publisher id, not a Vertex resource path: Langfuse prices by this
    // exact string. Needs `claude-opus-5` enabled in the Vertex Model Garden.
    id: "opus-5",
    label: "Claude Opus 5",
    provider: "anthropic",
    model: "claude-opus-5",
    reasoningEffort: "low",
    maxOutputTokens: 64000,
  },
];

/**
 * Model the executor runs on unless a caller names another entry.
 *
 * Which model production runs is no longer an edit to this constant: it is the
 * `excelUseClaudeOpus` ConfigCat flag, read once per run in `executorRunner`.
 * What this constant fixes is where every unresolved flag state lands. Keep
 * prior registry entries available so a future model router can select them
 * without another provider-surface migration.
 */
export const DEFAULT_EXECUTOR_MODEL_ID = "gpt-6-astra";

/** Model served when `excelUseClaudeOpus` is enabled for the current user. */
export const FLAGGED_EXECUTOR_MODEL_ID = "opus-5";

/**
 * Resolves a model id to its config, falling back to the default entry.
 *
 * The optional `id` lets a caller pin a specific model (tests select the
 * non-default provider this way). Resolution never throws on an unknown id: a
 * stale selector degrades to the default rather than failing the run.
 */
export function resolveExecutorModel(id?: string | null): ExecutorModelConfig {
  const requested = id?.trim();
  const match = requested
    ? EXECUTOR_MODELS.find((entry) => entry.id === requested)
    : undefined;
  if (match) return match;
  const fallback = EXECUTOR_MODELS.find(
    (entry) => entry.id === DEFAULT_EXECUTOR_MODEL_ID,
  );
  if (!fallback) {
    throw new Error(
      `Executor model registry is missing its default entry "${DEFAULT_EXECUTOR_MODEL_ID}"`,
    );
  }
  return fallback;
}
